/**
 * SoundManager - Centralized audio management for the game
 *
 * Features:
 * - Singleton pattern for global access
 * - Howler.js for cross-browser audio support
 * - Procedurally generated 8-bit sounds (no external files needed)
 * - Browser autoplay policy handling (unlocks on first user gesture)
 * - Volume and mute controls with localStorage persistence
 * - Rate limiting to prevent sound spam
 * - Type-safe sound definitions
 *
 * Quality considerations:
 * - Graceful degradation if sounds fail to load
 * - Memory-efficient: Howler handles audio pooling automatically
 * - Performance: Rate limiting prevents audio overload
 * - No external dependencies for sound files
 */

import { Howl, Howler } from 'howler';
import { generateAllSounds } from './SoundGenerator';

/**
 * Sound effect types available in the game
 */
export enum SoundType {
  // Gameplay sounds
  EAT = 'eat',
  EAT_COMBO = 'eat_combo',
  POWERUP = 'powerup',
  BOOST_START = 'boost_start',
  BOOST_LOOP = 'boost_loop',
  BOOST_END = 'boost_end',
  DEATH = 'death',
  KILL = 'kill',
  COMBO = 'combo',
  GROW = 'grow',
  SPAWN = 'spawn',
  LEVEL_UP = 'level_up',
  SLOW_MODE_START = 'slow_mode_start',
  SLOW_MODE_TICK = 'slow_mode_tick',

  // Feast sounds (merged food)
  FEAST_MEAL = 'feast_meal',
  FEAST_DINNER = 'feast_dinner',
  FEAST_FEAST = 'feast_feast',
  FEAST_BANQUET = 'feast_banquet',
  FEAST_GOLDEN = 'feast_golden',
  FEAST_MYTHICAL = 'feast_mythical',

  // UI sounds
  UI_CLICK = 'ui_click',
  UI_HOVER = 'ui_hover',
  UI_SUCCESS = 'ui_success',
  UI_ERROR = 'ui_error',
  GAME_START = 'game_start',

  // LEVIATHAN sounds
  LEVIATHAN_SPAWN = 'leviathan_spawn',
  LEVIATHAN_DEATH = 'leviathan_death',
  LEVIATHAN_NEARBY = 'leviathan_nearby',

  // Near-miss feedback
  NEAR_MISS = 'near_miss',
  NEAR_MISS_CLOSE = 'near_miss_close',
}

/**
 * Sound configuration
 */
interface SoundConfig {
  /** Base volume for this sound (0-1) */
  volume: number;
  /** Whether this sound should loop */
  loop?: boolean;
  /** Minimum time between plays (rate limiting in ms) */
  rateLimit?: number;
  /** Optional pitch variation range [min, max] */
  pitchVariation?: [number, number];
}

/**
 * Sound definitions - maps SoundType to configuration
 */
const SOUND_CONFIGS: Record<SoundType, SoundConfig> = {
  // Gameplay sounds
  [SoundType.EAT]: {
    volume: 0.5, // Bumped up for more satisfying feedback
    rateLimit: 40, // Slightly faster for rapid eating
    pitchVariation: [0.85, 1.15], // More variation for variety
  },
  [SoundType.EAT_COMBO]: {
    volume: 0.55,
    rateLimit: 80,
    pitchVariation: [0.9, 1.2],
  },
  [SoundType.POWERUP]: {
    volume: 0.65,
    rateLimit: 200,
  },
  [SoundType.BOOST_START]: {
    volume: 0.55,
    rateLimit: 100,
  },
  [SoundType.BOOST_LOOP]: {
    volume: 0.25,
    loop: true,
  },
  [SoundType.BOOST_END]: {
    volume: 0.45,
    rateLimit: 100,
  },
  [SoundType.DEATH]: {
    volume: 0.8, // More dramatic
    rateLimit: 500,
  },
  [SoundType.KILL]: {
    volume: 0.7, // More rewarding
    rateLimit: 150,
  },
  [SoundType.COMBO]: {
    volume: 0.75,
    rateLimit: 250,
    pitchVariation: [0.7, 1.5], // Wider range for exciting escalation
  },
  [SoundType.GROW]: {
    volume: 0.35,
    rateLimit: 100, // Don't spam when growing fast
    pitchVariation: [0.9, 1.1],
  },
  [SoundType.SPAWN]: {
    volume: 0.6,
    rateLimit: 1000, // Only once per spawn
  },
  [SoundType.LEVEL_UP]: {
    volume: 0.7,
    rateLimit: 500,
  },
  [SoundType.SLOW_MODE_START]: {
    volume: 0.4,
    rateLimit: 2000, // Once when entering slow mode
  },
  [SoundType.SLOW_MODE_TICK]: {
    volume: 0.25,
    rateLimit: 400, // Gentle tick for score accumulation
    pitchVariation: [0.95, 1.05],
  },

  // Feast sounds (merged food - exciting high-value targets)
  [SoundType.FEAST_MEAL]: {
    volume: 0.45,
    rateLimit: 500,
  },
  [SoundType.FEAST_DINNER]: {
    volume: 0.55,
    rateLimit: 500,
  },
  [SoundType.FEAST_FEAST]: {
    volume: 0.65,
    rateLimit: 500,
  },
  [SoundType.FEAST_BANQUET]: {
    volume: 0.75,
    rateLimit: 500,
  },
  [SoundType.FEAST_GOLDEN]: {
    volume: 0.85,
    rateLimit: 500,
  },
  [SoundType.FEAST_MYTHICAL]: {
    volume: 0.95,
    rateLimit: 500,
  },

  // UI sounds
  [SoundType.UI_CLICK]: {
    volume: 0.35,
    rateLimit: 50,
  },
  [SoundType.UI_HOVER]: {
    volume: 0.18,
    rateLimit: 30,
  },
  [SoundType.UI_SUCCESS]: {
    volume: 0.55,
    rateLimit: 200,
  },
  [SoundType.UI_ERROR]: {
    volume: 0.5,
    rateLimit: 200,
  },
  [SoundType.GAME_START]: {
    volume: 0.6,
    rateLimit: 1000,
  },

  // LEVIATHAN sounds (boss-specific)
  [SoundType.LEVIATHAN_SPAWN]: {
    volume: 0.9, // Loud announcement
    rateLimit: 5000, // Only once per spawn
  },
  [SoundType.LEVIATHAN_DEATH]: {
    volume: 0.9, // Loud celebration
    rateLimit: 5000, // Only once per death
  },
  [SoundType.LEVIATHAN_NEARBY]: {
    volume: 0.4, // Subtle tension
    rateLimit: 3000, // Periodic heartbeat
    loop: false,
  },

  // Near-miss feedback (whoosh sounds)
  [SoundType.NEAR_MISS]: {
    volume: 0.4, // Noticeable but not annoying
    rateLimit: 300, // Prevent spam but allow frequent near-misses
    pitchVariation: [0.9, 1.1],
  },
  [SoundType.NEAR_MISS_CLOSE]: {
    volume: 0.6, // Louder for very close calls
    rateLimit: 300,
    pitchVariation: [0.85, 1.05],
  },
};

/** localStorage keys */
const STORAGE_KEY_MUTED = 'wooorms_sound_muted';
const STORAGE_KEY_VOLUME = 'wooorms_sound_volume';

/**
 * SoundManager - Singleton for game audio management
 */
export class SoundManager {
  private static instance: SoundManager | null = null;

  /** Loaded Howl instances */
  private sounds: Map<SoundType, Howl> = new Map();

  /** Currently playing looping sound IDs */
  private loopingSounds: Map<SoundType, number> = new Map();

  /** Last play time for rate limiting */
  private lastPlayTime: Map<SoundType, number> = new Map();

  /** Master volume (0-1) */
  private _volume: number = 0.7;

  /** Muted state */
  private _muted: boolean = false;

  /** Whether audio has been unlocked (user gesture received) */
  private _unlocked: boolean = false;

  /** Loading state */
  private _loaded: boolean = false;
  private _loadErrors: string[] = [];

  /** Whether sounds have been generated (deferred until user interaction) */
  private _initialized: boolean = false;

  /** Pending initialization promise */
  private _initPromise: Promise<void> | null = null;

  /** Event listeners for state changes */
  private listeners: Set<(muted: boolean, volume: number) => void> = new Set();

  private constructor() {
    this.loadSettings();
    // Don't touch Howler here - defer until user interaction to avoid AudioContext warning
    this.setupUnlockListeners();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  /**
   * Initialize and generate all sounds
   * This is now called automatically after user interaction to avoid AudioContext warnings
   * Can also be called manually, but sounds won't play until user interacts
   */
  async init(): Promise<void> {
    // If already initialized or initializing, return existing promise
    if (this._initPromise) return this._initPromise;
    if (this._initialized) return;

    this._initPromise = this.doInit();
    return this._initPromise;
  }

  /**
   * Internal initialization - generates sounds and sets up Howler
   */
  private async doInit(): Promise<void> {
    if (this._loaded) return;

    try {
      // Generate all sounds procedurally
      const soundDataUrls = generateAllSounds();

      // Create Howl instances for each sound
      const loadPromises: Promise<void>[] = [];

      for (const [soundName, dataUrl] of soundDataUrls) {
        const soundType = soundName as SoundType;
        const config = SOUND_CONFIGS[soundType];

        if (!config) {
          console.warn(`[SoundManager] No config for sound: ${soundName}`);
          continue;
        }

        const promise = this.loadSound(soundType, dataUrl, config);
        loadPromises.push(promise);
      }

      await Promise.allSettled(loadPromises);

      this._loaded = true;
      this._initialized = true;

      // Now it's safe to apply volume since we're inside user gesture
      this.applyGlobalVolume();

      if (this._loadErrors.length > 0) {
        console.warn('[SoundManager] Some sounds failed to load:', this._loadErrors);
      }
    } catch (error) {
      console.error('[SoundManager] Failed to initialize sounds:', error);
    }
  }

  /**
   * Load a single sound from data URL
   */
  private loadSound(type: SoundType, dataUrl: string, config: SoundConfig): Promise<void> {
    return new Promise((resolve) => {
      const howl = new Howl({
        src: [dataUrl],
        format: ['wav'],
        volume: config.volume * this._volume,
        loop: config.loop ?? false,
        preload: true,
        onload: () => {
          this.sounds.set(type, howl);
          resolve();
        },
        onloaderror: (_id, error) => {
          this._loadErrors.push(`${type}: ${error}`);
          resolve(); // Don't reject - graceful degradation
        },
      });
    });
  }

  /**
   * Set up listeners to unlock audio on first user gesture
   * Required for Chrome/Safari autoplay policy compliance
   * Also triggers deferred sound initialization
   */
  private setupUnlockListeners(): void {
    const unlockEvents = ['click', 'touchstart', 'keydown'];

    const unlock = () => {
      if (this._unlocked) return;

      this._unlocked = true;

      // Initialize sounds now that we have user gesture
      // This creates AudioContext inside the gesture handler, avoiding the warning
      this.init().then(() => {
        // Resume Howler's audio context if it got suspended
        if (Howler.ctx && Howler.ctx.state === 'suspended') {
          Howler.ctx.resume();
        }
      });

      // Remove listeners after unlock
      unlockEvents.forEach((event) => {
        document.removeEventListener(event, unlock, true);
      });
    };

    // Add listeners with capture to catch all interactions
    unlockEvents.forEach((event) => {
      document.addEventListener(event, unlock, true);
    });
  }

  /**
   * Play a sound effect
   * @param type The sound to play
   * @param options Optional overrides
   * @returns The sound ID if played, null if rate-limited or unavailable
   */
  play(
    type: SoundType,
    options?: { volume?: number; pitch?: number }
  ): number | null {
    // Don't play if muted
    if (this._muted) return null;

    // Check rate limiting
    const config = SOUND_CONFIGS[type];
    if (config.rateLimit) {
      const now = Date.now();
      const lastPlay = this.lastPlayTime.get(type) ?? 0;
      if (now - lastPlay < config.rateLimit) {
        return null; // Rate limited
      }
      this.lastPlayTime.set(type, now);
    }

    // Get the sound
    const howl = this.sounds.get(type);
    if (!howl) {
      // Sound not loaded - graceful degradation
      return null;
    }

    // Calculate volume
    const volume = (options?.volume ?? 1) * config.volume * this._volume;

    // Calculate pitch (rate)
    let rate = options?.pitch ?? 1;
    if (config.pitchVariation && !options?.pitch) {
      const [min, max] = config.pitchVariation;
      rate = min + Math.random() * (max - min);
    }

    // Play the sound
    const id = howl.play();
    howl.volume(volume, id);
    howl.rate(rate, id);

    return id;
  }

  /**
   * Play a sound with pitch based on a value (e.g., combo count)
   * Higher values = higher pitch
   */
  playWithPitch(type: SoundType, value: number, minPitch = 0.8, maxPitch = 1.5): number | null {
    // Map value to pitch range (1-10 maps to minPitch-maxPitch)
    const normalizedValue = Math.min(10, Math.max(1, value));
    const pitch = minPitch + ((normalizedValue - 1) / 9) * (maxPitch - minPitch);
    return this.play(type, { pitch });
  }

  /**
   * Start a looping sound
   * @param type The sound to loop (must be configured with loop: true)
   * @returns The sound ID if started
   */
  startLoop(type: SoundType): number | null {
    if (this._muted) return null;

    // Stop existing loop of this type
    this.stopLoop(type);

    const howl = this.sounds.get(type);
    if (!howl) return null;

    const config = SOUND_CONFIGS[type];
    const volume = config.volume * this._volume;

    const id = howl.play();
    howl.volume(volume, id);
    howl.loop(true, id);

    this.loopingSounds.set(type, id);
    return id;
  }

  /**
   * Stop a looping sound
   */
  stopLoop(type: SoundType): void {
    const id = this.loopingSounds.get(type);
    if (id === undefined) return;

    const howl = this.sounds.get(type);
    if (howl) {
      howl.stop(id);
    }

    this.loopingSounds.delete(type);
  }

  /**
   * Stop all looping sounds
   */
  stopAllLoops(): void {
    for (const type of this.loopingSounds.keys()) {
      this.stopLoop(type);
    }
  }

  /**
   * Get current muted state
   */
  get muted(): boolean {
    return this._muted;
  }

  /**
   * Set muted state
   */
  set muted(value: boolean) {
    this._muted = value;
    this.saveSettings();
    this.applyGlobalVolume();

    // Stop all loops when muting
    if (value) {
      this.stopAllLoops();
    }

    this.notifyListeners();
  }

  /**
   * Toggle muted state
   */
  toggleMute(): boolean {
    this.muted = !this._muted;
    return this._muted;
  }

  /**
   * Get current volume (0-1)
   */
  get volume(): number {
    return this._volume;
  }

  /**
   * Set master volume (0-1)
   */
  set volume(value: number) {
    this._volume = Math.max(0, Math.min(1, value));
    this.saveSettings();
    this.applyGlobalVolume();
    this.notifyListeners();
  }

  /**
   * Apply global volume to Howler
   */
  private applyGlobalVolume(): void {
    Howler.volume(this._muted ? 0 : this._volume);
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): void {
    try {
      const mutedStr = localStorage.getItem(STORAGE_KEY_MUTED);
      if (mutedStr !== null) {
        this._muted = mutedStr === 'true';
      }

      const volumeStr = localStorage.getItem(STORAGE_KEY_VOLUME);
      if (volumeStr !== null) {
        const volume = parseFloat(volumeStr);
        if (!isNaN(volume)) {
          this._volume = Math.max(0, Math.min(1, volume));
        }
      }
    } catch {
      // localStorage not available - use defaults
    }
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY_MUTED, String(this._muted));
      localStorage.setItem(STORAGE_KEY_VOLUME, String(this._volume));
    } catch {
      // localStorage not available - ignore
    }
  }

  /**
   * Add a listener for state changes (muted/volume)
   */
  addListener(callback: (muted: boolean, volume: number) => void): void {
    this.listeners.add(callback);
  }

  /**
   * Remove a listener
   */
  removeListener(callback: (muted: boolean, volume: number) => void): void {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    for (const callback of this.listeners) {
      callback(this._muted, this._volume);
    }
  }

  /**
   * Check if audio is unlocked (user has interacted)
   */
  get isUnlocked(): boolean {
    return this._unlocked;
  }

  /**
   * Check if sounds are loaded
   */
  get isLoaded(): boolean {
    return this._loaded;
  }

  /**
   * Get any load errors (for debugging)
   */
  get loadErrors(): string[] {
    return [...this._loadErrors];
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopAllLoops();

    for (const howl of this.sounds.values()) {
      howl.unload();
    }

    this.sounds.clear();
    this.loopingSounds.clear();
    this.lastPlayTime.clear();
    this.listeners.clear();

    SoundManager.instance = null;
  }
}

/**
 * Convenience function to get the SoundManager instance
 */
export function getSoundManager(): SoundManager {
  return SoundManager.getInstance();
}
